import { Box, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../../utils/colors';
import { FBoxContainer } from '../../../../base';
import { usDollarFormatter } from '../../../../../helpers';
import { PayorResponsibilityEnum } from '../../../../../constants';

import type { RootState } from '../../../../../store';

interface IOverviewCardProps {
  title: string;
  payment: number;
  balance: number;
  primaryInsuranceBalance: number;
  secondaryInsuranceBalance: number;
  hasSecondaryPayor: boolean;
}

function OverviewCard({
  title,
  payment,
  balance,
  primaryInsuranceBalance,
  secondaryInsuranceBalance,
  hasSecondaryPayor,
}: IOverviewCardProps) {
  return (
    <Box sx={{ backgroundColor: FORTA_WHITE, borderRadius: '8px', px: 2, py: 2.5 }}>
      <Typography fontWeight={700} color={FORTA_BLUE.primary}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <Box sx={{ width: '50%' }}>
          <Typography fontWeight={700} mb={1}>
            Total Payment
          </Typography>
          <Typography fontWeight={400}>{usDollarFormatter.format(payment ?? 0)}</Typography>
        </Box>
        <Box>
          <Typography fontWeight={700} mb={1}>
            Total Balance
          </Typography>
          <Typography
            fontWeight={balance! > 0 ? 700 : 400}
            color={balance! > 0 ? FORTA_ERROR : FORTA_BLACK}
          >
            {usDollarFormatter.format(balance ?? 0)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <Box sx={{ width: '50%' }}>
          <Typography fontWeight={700} mb={1}>
            Primary payor balance
          </Typography>
          <Typography
            fontWeight={primaryInsuranceBalance! > 0 ? 700 : 400}
            color={primaryInsuranceBalance! > 0 ? FORTA_ERROR : FORTA_BLACK}
          >
            {usDollarFormatter.format(primaryInsuranceBalance ?? 0)}
          </Typography>
        </Box>
        <Box>
          <Typography fontWeight={700} mb={1}>
            Secondary payor balance
          </Typography>
          {hasSecondaryPayor ? (
            <Typography
              fontWeight={secondaryInsuranceBalance! > 0 ? 700 : 400}
              color={secondaryInsuranceBalance! > 0 ? FORTA_ERROR : FORTA_BLACK}
            >
              {usDollarFormatter.format(secondaryInsuranceBalance ?? 0)}
            </Typography>
          ) : (
            <Typography>N/A</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function PaymentOverview() {
  const { current: claim } = useSelector((state: RootState) => state.claims);
  const hasSecondaryPayor =
    claim?.policies?.some(
      (policy) => policy.payor_responsibility === PayorResponsibilityEnum.SECONDARY,
    ) ?? false;

  return claim ? (
    <FBoxContainer sx={{ my: 3, position: 'relative', borderRadius: '10px', boxShadow: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          justifyContent: 'space-between',
        }}
      >
        <Typography fontSize={21} color={FORTA_BLUE.primary} textAlign="left">
          Overview
        </Typography>
        <Box sx={{ backgroundColor: FORTA_WHITE, py: 0.7, px: 1.5, borderRadius: '20px' }}>
          <Typography display="inline" fontWeight={700} color={FORTA_BLUE.primary}>
            {claim?.service_code?.type}:
          </Typography>
          <Typography display="inline" color={FORTA_BLUE.primary}>
            {` ${claim?.service_code?.code}`}
          </Typography>
        </Box>
      </Box>

      <Grid container textAlign="left" mt={1} spacing={3}>
        <Grid item xs={12} md={6} mt={1}>
          <Box sx={{ backgroundColor: FORTA_WHITE, borderRadius: '8px', px: 2, py: 2.5 }}>
            <Typography fontWeight={700} color={FORTA_BLUE.primary}>
              Claim
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
              <Box sx={{ width: '50%' }}>
                <Typography fontWeight={700} mb={1}>
                  Amount billed
                </Typography>
                <Typography>{usDollarFormatter.format(claim.billed ?? 0)}</Typography>
              </Box>
              <Box sx={{ width: '50%' }}>
                <Typography fontWeight={700} mb={1}>
                  Status
                </Typography>
                <Typography>{claim.status}</Typography>
              </Box>
              <Box sx={{ width: '50%', mt: 2 }}>
                <Typography fontWeight={700} mb={1} mr={1}>
                  Patient responsibility
                </Typography>
                <Typography>
                  {usDollarFormatter.format(claim?.patient_responsibility ?? 0)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} mt={1}>
          <OverviewCard
            title="Insurance"
            payment={claim.amount_insurance_paid}
            balance={claim.balance}
            primaryInsuranceBalance={claim.primary_insurance_balance}
            secondaryInsuranceBalance={claim.secondary_insurance_balance}
            hasSecondaryPayor={hasSecondaryPayor}
          />
        </Grid>
      </Grid>
    </FBoxContainer>
  ) : null;
}

export default PaymentOverview;
